import { formatDate } from '@brimo/shared';
import { Skeleton } from '../../../../ds/components';
import { useIsMobile } from '../../../../ds/hooks';
import { errorMessage } from '../../../../lib/api';
import { useUserBilling } from '../../../settings/billing/api';
import { BillingModeCard, PaymentMethodForm, PlanCard } from '../../../settings/billing/ui';
import { WizardButton } from '../../ui';
import { ActionError, StepFrame, useWizard } from '../StepFrame';
import styles from './BillingStep.module.css';

/** „30.09." of a deadline day (Nutzer 10 „Bis 30.09. nachholen"). */
function dayMonth(day: string): string {
  return formatDate(day).slice(0, 6);
}

/**
 * Nutzer 10 / M8. Own billing: the plan line and F3's payment form (the server validates, then answers 409
 * `PAYMENTS_NOT_ACTIVE` — the form shows it), „Bis … nachholen" skips to the deadline. Company billing: the variant
 * card „Kein Zahlungsmittel nötig" with „Weiter zur Sicherheit".
 */
export function BillingStep() {
  const mobile = useIsMobile();
  const wizard = useWizard();
  const billing = useUserBilling();
  const data = billing.data;
  const back = wizard.goBack ? { label: 'Zurück', onClick: wizard.goBack } : undefined;

  if (!data) {
    return (
      <StepFrame title="Zahlungsmittel hinterlegen" width={620} footer={null}>
        {billing.isError ? (
          <p className={styles.error}>{errorMessage(billing.error)}</p>
        ) : (
          <Skeleton variant="block" height={420} />
        )}
      </StepFrame>
    );
  }

  if (data.mode === 'company') {
    return (
      <StepFrame
        title="Kein Zahlungsmittel nötig"
        intro={false}
        width={mobile ? 390 : 400}
        footer={null}
        mobileFooter={{
          primary: {
            label: 'Weiter zur Sicherheit',
            onClick: () => void wizard.act('continue'),
            loading: wizard.acting === 'continue',
          },
        }}
      >
        <BillingModeCard
          billing={data}
          variant="wizard"
          eyebrow="Schritt 7 von 8 · über die Firma abgerechnet"
          title="Kein Zahlungsmittel nötig"
          text={`${wizard.setup.user.organizationName} trägt die Kosten für Ihren Arbeitsplatz. Der Schritt wird übersprungen und erscheint nicht in der Checkliste.`}
          action={
            mobile ? undefined : (
              <WizardButton
                variant="primary"
                loading={wizard.acting === 'continue'}
                onClick={() => void wizard.act('continue')}
              >
                Weiter zur Sicherheit
              </WizardButton>
            )
          }
        />
        {back && !mobile ? (
          <div>
            <WizardButton variant="secondary" onClick={back.onClick}>
              {back.label}
            </WizardButton>
          </div>
        ) : null}
        <ActionError />
      </StepFrame>
    );
  }

  const deadline = data.deadlineOn ? `Bis ${dayMonth(data.deadlineOn)} nachholen` : 'Später nachholen';
  return (
    <StepFrame
      eyebrow="Schritt 7 von 8 · eigene Abrechnung"
      title="Zahlungsmittel hinterlegen"
      text={`Ihre Administration rechnet Ihren Platz auf Nutzerebene ab. Erste Abbuchung am ${formatDate(data.plan.firstDebitOn)}.`}
      mobileTitle="Zahlungsmittel"
      mobileText={`Ihr Platz wird auf Nutzerebene abgerechnet · ${data.plan.priceNetLabel.replace(' / Monat', ' monatlich')}.`}
      width={620}
      gap={22}
      footer={null}
      mobileFooter={null}
    >
      <PaymentMethodForm
        header={
          mobile ? undefined : (
            <PlanCard
              variant="compact"
              name={data.plan.name}
              detail={data.plan.note}
              priceLabel={data.plan.priceNetLabel}
            />
          )
        }
        secondary={
          back && !mobile ? (
            <WizardButton variant="secondary" onClick={back.onClick}>
              {back.label}
            </WizardButton>
          ) : undefined
        }
        deadlineLabel={mobile ? 'Später' : deadline}
        onDefer={() => void wizard.act('skip')}
        submitLabel="Speichern"
        defaultAddress={data.billingAddress ?? ''}
        defaultVatId={data.vatId ?? ''}
        defaultHolder={`${wizard.setup.user.firstName} ${wizard.setup.user.lastName}`.trim()}
      />
      <ActionError />
    </StepFrame>
  );
}
